import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import "./index.css";
import { useNavigate } from "react-router-dom";
import { IoIosHome } from "react-icons/io";
import Select from "react-select";

const eventNameMapping = {
  login_attempt: "Login Attempt",
  login_success: "Login Successful",
  login_failed: "Login Failed",
  user_logged_out: "User Logged Out",
  signup_link_clicked: "Sign-up Button Clicked",
  sign_in_attempt: "Sign-in Attempt",
  registration_attempt: "New Registration Attempt",
  registration_failed: "Registration Failed",
  accepted_disclaimer: "Accepted Disclaimer",
  rejected_disclaimer: "Rejected Disclaimer",
  landing_page_view_summary: "View Summary Clicked on Search Results",
  registrar_id_clicked: "Registrar ID Clicked",
  heiarchy_graph_clicked: "Hierarchy Graph Clicked",
  share_clicked: "Share Button Clicked",
  download_png: "Downloaded PNG",
  download_pdf: "Downloaded PDF",
  download_svg: "Downloaded SVG",
  download_excel: "Exported Excel",
  back_button_clicked: "Back Navigation",
  home_button_clicked: "Home Navigation",
  navigate_dashboard: "Navigated to Dashboard",
  orientation_changed: "Graph Orientation Changed",
  graph_summary_node_clicked:
    "Clicked on Selected Entity on Graph Hierarchy",
};

const columnHeaderMapping = {
  sessionId: "Session ID",
  userId: "User Email",
  eventName: "Event Name",
  timestamp: "Timestamp (US)",
  ipAddress: "IP Address",
  page: "Page",
  details: "Details",
};

const formatTimestamp = (ts) => {
  const date = new Date(ts);
  return date.toLocaleString("en-US", { timeZone: "America/New_York" });
};

const AnalyticsTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [columnFilters, setColumnFilters] = useState({});
  const [selectedUser, setSelectedUser] = useState(null); // ✅ NEW

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(
        "https://jmn1qjmpe9.execute-api.us-east-2.amazonaws.com/dev",
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to fetch analytics data");
      }

      const analyticsData = await response.json();

      const mappedData = analyticsData.map((item) => ({
        ...item,
        eventName: eventNameMapping[item.eventName] || item.eventName,
        timestampRaw: item.timestamp,
        timestamp: formatTimestamp(item.timestamp),
      }));

      mappedData.sort(
        (a, b) => new Date(b.timestampRaw) - new Date(a.timestampRaw)
      );

      setData(mappedData);
      setFilteredData(mappedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // ✅ UNIQUE USERS
  const userOptions = [
    ...new Set(data.map((item) => item.userId)),
  ].map((user) => ({ value: user, label: user }));

  // ✅ FILTER LOGIC (combined)
  useEffect(() => {
    let filtered = [...data];

    if (selectedUser) {
      filtered = filtered.filter(
        (row) => row.userId === selectedUser.value
      );
    }

    Object.keys(columnFilters).forEach((key) => {
      const filterValue = columnFilters[key]?.trim().toLowerCase();
      if (!filterValue) return;

      filtered = filtered.filter((row) => {
        let cell = row[key];

        if (key === "details" && typeof cell === "object") {
          cell = JSON.stringify(cell).toLowerCase();
        } else {
          cell = cell ? String(cell).toLowerCase() : "";
        }

        return cell.includes(filterValue);
      });
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [selectedUser, columnFilters, data]);

  const handleColumnFilterChange = (col, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [col]: value,
    }));
  };

  // ✅ CLEAR FILTERS
  const clearFilters = () => {
    setColumnFilters({});
    setSelectedUser(null);
  };

  const exportToExcel = () => {
    const exportData = filteredData.map((item) => ({
      ...item,
      eventName: eventNameMapping[item.eventName] || item.eventName,
      details:
        typeof item.details === "object"
          ? JSON.stringify(item.details)
          : item.details || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    ws["!cols"] = [
      { wch: 25 },
      { wch: 30 },
      { wch: 25 },
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 40 },
      { wch: 40 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AnalyticsData");
    XLSX.writeFile(wb, "Glue_User_Analytics.xlsx");
  };

  if (isLoading)
    return <div className="analytics-message loading">Loading analytics data...</div>;
  if (error)
    return <div className="analytics-message error">Error: {error}</div>;
  if (data.length === 0)
    return <div className="analytics-message">No analytics events found.</div>;

  const columns = Object.keys(columnHeaderMapping);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirst, indexOfLast);

  const handleBack = () => navigate("/glue-dashboard");

  return (
    <>
      <div className="analytics-container">
        <div className="analytics-header">
          <div style={{ display: "flex", alignItems: "center" }}>
            <IoIosHome size={25} color="#2a3c7b" onClick={handleBack} />
            <h2 style={{ paddingLeft: 10 }}>Glue User Analytics Data</h2>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="export-btn" onClick={fetchAnalytics}>
              Get Latest Data
            </button>

            <button className="export-btn" onClick={exportToExcel}>
              Export to Excel
            </button>

            {/* ✅ SEARCHABLE DROPDOWN */}
            <div style={{ minWidth: 220 }}>
              <Select
                options={userOptions}
                value={selectedUser}
                onChange={setSelectedUser}
                isClearable
                placeholder="Filter by User Email"
              />
            </div>

            {/* ✅ CLEAR FILTER */}
            <button className="export-btn" onClick={clearFilters}>
              Clear Filters
            </button>

            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div className="analytics-table-container">
          <table className="analytics-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>
                    {columnHeaderMapping[col]}
                    <input
                      className="col-filter-input"
                      type="text"
                      placeholder="Search..."
                      value={columnFilters[col] || ""}
                      onChange={(e) =>
                        handleColumnFilterChange(col, e.target.value)
                      }
                    />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {currentRows.map((item, index) => (
                <tr key={index}>
                  {columns.map((col) => (
                    <td key={col}>
                      {col === "details" && typeof item[col] === "object"
                        ? JSON.stringify(item[col])
                        : item[col]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ✅ PAGINATION NUMBERS */}
        <div className="pagination">
          {/* Previous */}
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Prev
          </button>

          {/* First pages */}
          {[1, 2, 3].map(
            (num) =>
              num <= totalPages && (
                <button
                  key={num}
                  className={currentPage === num ? "active-page" : ""}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              )
          )}

          {/* Ellipsis */}
          {currentPage > 5 && <span>...</span>}

          {/* Middle pages around current */}
          {Array.from({ length: 3 }, (_, i) => currentPage - 1 + i).map((num) => {
            if (num > 3 && num < totalPages - 2) {
              return (
                <button
                  key={num}
                  className={currentPage === num ? "active-page" : ""}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              );
            }
            return null;
          })}

          {/* Ellipsis */}
          {currentPage < totalPages - 4 && <span>...</span>}

          {/* Last pages */}
          {[totalPages - 2, totalPages - 1, totalPages].map(
            (num) =>
              num > 3 &&
              num <= totalPages && (
                <button
                  key={num}
                  className={currentPage === num ? "active-page" : ""}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              )
          )}

          {/* Next */}
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>

          {/* Jump to page */}
          <input
            type="number"
            min="1"
            max={totalPages}
            placeholder="Go to"
            style={{ width: 70, marginLeft: 10 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                let val = Number(e.target.value);
                if (val >= 1 && val <= totalPages) {
                  setCurrentPage(val);
                }
              }
            }}
          />
        </div>

        <style>{`
        .pagination span {
          padding: 6px 4px;
        }

        .col-filter-input{
          width:95%;
          margin-top:6px;
          padding:6px;
          font-size:13px;
        }
        .pagination{
          margin-top:15px;
          display:flex;
          gap:6px;
          justify-content:center;
          flex-wrap:wrap;
        }
        .pagination button{
          padding:6px 10px;
          border-radius:6px;
          border:1px solid #ccc;
          cursor:pointer;
        }
        .pagination button.active-page{
          background:#2a3c7b;
          color:white;
        }
      `}</style>
      </div>
    </>
  );
};

export default AnalyticsTable;
